<script setup>
const slots = useSlots();
const props = defineProps({
  title: String,
  isLoading: Boolean,
  isEmpty: {
    type: Boolean,
    default: false,
  },
  buttonLabel: {
    type: String,
  },
  buttonLink: {
    type: String,
  },
});

// template refs
const carousel = ref(null);
const carouselScrollState = reactive({
  atStart: true,
  atEnd: false,
});

const checkScrollPosition = () => {
  if (carousel.value) {
    const maxScrollLeft =
      carousel.value.scrollWidth - carousel.value.clientWidth;
    carouselScrollState.atStart = carousel.value.scrollLeft === 0;
    carouselScrollState.atEnd = carousel.value.scrollLeft >= maxScrollLeft - 10;
  }
};

const listScroll = (direction = "right") => {
  if (carousel.value) {
    carousel.value.scrollBy({
      left: direction === "left" ? -400 : 400,
      behavior: "smooth",
    });
  }
};

onMounted(() => {
  carousel.value.addEventListener("scroll", checkScrollPosition);
  checkScrollPosition();
  window.addEventListener('resize', checkScrollPosition);
});

onBeforeUnmount(() => {
  if (carousel.value) {
    carousel.value.removeEventListener("scroll", checkScrollPosition);
  }
  window.removeEventListener('resize', checkScrollPosition);
});

watchEffect(() => {
  checkScrollPosition();
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between gap-5" v-if="!isEmpty">
      <div class="flex items-center gap-4">
        <h1 class="text-sm font-semibold text-white">
          <template v-if="title">{{ title }}</template>
          <span
            v-if="!title"
            class="shimmer-effect flex h-4 w-44 rounded"
          ></span>
        </h1>

        <div class="hidden gap-5 sm:flex">
          <div class="hidden items-end gap-3 lg:flex">
            <button
              :disabled="carouselScrollState.atStart"
              @click="listScroll('left')"
              :class="{
                hidden:
                  carouselScrollState.atStart && carouselScrollState.atEnd,
              }"
            >
              <Icon
                name="ep:arrow-right-bold"
                class="rotate-180"
                :class="{
                  'cursor-not-allowed text-gray-400':
                    carouselScrollState.atStart,
                  'hover:text-brand-primary': !carouselScrollState.atStart,
                }"
                size="12"
              />
            </button>
            <button
              :disabled="carouselScrollState.atEnd"
              @click="listScroll('right')"
              :class="{
                hidden:
                  carouselScrollState.atStart && carouselScrollState.atEnd,
              }"
            >
              <Icon
                name="ep:arrow-right-bold"
                class=""
                :class="{
                  'cursor-not-allowed text-gray-400': carouselScrollState.atEnd,
                  'hover:text-brand-primary': !carouselScrollState.atEnd,
                }"
                size="12"
              />
            </button>
          </div>
        </div>
      </div>

      <BaseButton
        size="xs"
        class="text-gray-400 transition-all hover:text-brand-primary"
        icon-after="fa6-solid:angle-right"
        :to="buttonLink"
      >
        {{ buttonLabel }}
      </BaseButton>
    </div>

    <div
      class="scrollbar-hide relative -ml-4 w-screen sm:-ml-6 md:-ml-6 lg:ml-0 lg:w-full"
    >
      <!-- Left fade gradient that shows when scrolled from start -->
      <div
        class="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-10 bg-gradient-to-l from-transparent to-brand-background transition-opacity duration-200"
        :class="{ 'opacity-0': carouselScrollState.atStart, 'opacity-100': !carouselScrollState.atStart }"
      ></div>
      
      <!-- Right fade gradient that conditionally shows based on scroll position -->
      <div
        class="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-10 bg-gradient-to-r from-transparent to-brand-background transition-opacity duration-200"
        :class="{ 'opacity-0': carouselScrollState.atEnd, 'opacity-100': !carouselScrollState.atEnd }"
      ></div>
      <!-- show items length of the carousel -->
      <div
        class="scrollbar-hide flex max-w-screen-xl gap-4 overflow-x-auto overflow-y-hidden pt-3 before:flex before:h-4 before:w-[1px] before:content-[''] after:flex after:h-4 after:w-4 after:content-[''] md:gap-5 before:lg:hidden after:lg:hidden"
        ref="carousel"
      >
        <slot :is-loading="isLoading" />
      </div>
    </div>
  </div>
</template>